import { SyntheticEvent, useMemo } from 'react';
import { Typography } from '@mui/material';
import { Alert, Link } from '@elementary/common-ui/components';
import useIncidentOverviewSidePanel from 'incidents/hooks/useIncidentOverviewSidePanel';
import type { IncidentDetail } from '../../types';

type Props = {
  incident: IncidentDetail;
};

type SplitIncidentReference = {
  id: string;
  title: string;
};

const getSplitChildIncidents = (
  incident: IncidentDetail,
): SplitIncidentReference[] => {
  const seen = new Set<string>();
  const splitChildren: SplitIncidentReference[] = [];
  for (const event of incident.events) {
    if (event.type !== 'SIGNAL_REMOVED') {
      continue;
    }
    const data = event.data as Record<string, unknown> | null;
    const relatedIncidentId = data?.related_incident_id;
    if (typeof relatedIncidentId !== 'string' || seen.has(relatedIncidentId)) {
      continue;
    }
    seen.add(relatedIncidentId);
    const relatedTitle = data?.related_incident_title;
    splitChildren.push({
      id: relatedIncidentId,
      title:
        typeof relatedTitle === 'string' && relatedTitle
          ? relatedTitle
          : 'another incident',
    });
  }
  return splitChildren;
};

const SplitIncidentAlert = ({ incident }: Props) => {
  const { onOpen } = useIncidentOverviewSidePanel();
  const splitChildren = useMemo(
    () => getSplitChildIncidents(incident),
    [incident],
  );

  if (splitChildren.length === 0) {
    return null;
  }

  const handleOpen = (incidentId: string) => (event: SyntheticEvent) => {
    event.preventDefault();
    onOpen(incidentId);
  };

  return (
    <Alert severity="info" sx={{ flexShrink: 0 }}>
      {splitChildren.length === 1 ? (
        <Typography component="p" variant="body2" sx={{ m: 0 }}>
          This incident was split into{' '}
          <Link
            href="#"
            onClick={handleOpen(splitChildren[0].id)}
            style={{ fontWeight: 400 }}
          >
            {splitChildren[0].title}
          </Link>
          .
        </Typography>
      ) : (
        <>
          <Typography component="p" variant="body2" sx={{ m: 0 }}>
            This incident was split into {splitChildren.length} incidents:
          </Typography>
          <Typography component="ul" variant="body2" sx={{ m: 0, pl: 3 }}>
            {splitChildren.map((splitChild) => (
              <li key={splitChild.id}>
                <Link
                  href="#"
                  onClick={handleOpen(splitChild.id)}
                  style={{ fontWeight: 400 }}
                >
                  {splitChild.title}
                </Link>
              </li>
            ))}
          </Typography>
        </>
      )}
    </Alert>
  );
};

export default SplitIncidentAlert;
